'use client';

import { User, getRedirectResult, onIdTokenChanged } from 'firebase/auth';
import { doc, setDoc, Timestamp, DocumentReference, getDoc } from 'firebase/firestore';
import { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { AppLoadingSpinner } from '@/components/shared/AppLoadingSpinner';
import { syncResearchChatRuntimeOwner } from '@/hooks/useResearchChat';
import {
  clearServerSession,
  consumePendingSignOutContext,
  ensureServerSessionForUser,
  isTerminalAuthSessionError,
  setAuthSessionDiagnosticsReporter,
  signOutAndClearSession,
  syncServerSessionForUser,
} from '@/lib/auth-session';
import { auth, authPersistenceReady, db } from '@/lib/firebase';
import { identify, reset, capture, setPersonProperties } from '@/lib/posthog';
import { UserDocument } from '@/types/database-schema';

interface AuthContextType {
  user: User | null;
  userDocument: UserDocument | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userDocument: null,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

const SESSION_VISIBILITY_RECOVERY_THRESHOLD_MS = 15 * 60 * 1000;

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function handleSessionRecoveryError(error: unknown): void {
  if (isAbortError(error)) {
    return;
  }

  if (isTerminalAuthSessionError(error)) {
    void signOutAndClearSession(auth, 'credentials_invalid');
    return;
  }

  console.error('Error maintaining server auth session:', error);
}

let desktopUpdateTokenVersion = 0;

async function syncDesktopUpdateToken(user: User | null): Promise<void> {
  const version = ++desktopUpdateTokenVersion;
  const desktop = window.attuneDesktop;
  if (!desktop) return;
  try {
    const token = user ? await user.getIdToken() : null;
    // A later auth event (including sign-out) invalidates this asynchronous token read.
    if (version !== desktopUpdateTokenVersion || auth.currentUser !== user) return;
    const accepted = await desktop.setUpdateToken(token);
    if (!accepted) throw new Error('Desktop rejected the update credential.');
  } catch (error) {
    console.warn('Could not configure desktop updates:', error);
  }
}

const createUserDocument = async (user: User) => {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  const now = Timestamp.now();

  if (!userSnap.exists()) {
    // For new users, create the document with a `created_at` timestamp.
    const newUserDoc: Partial<UserDocument> = {
      uid: user.uid,
      email: user.email || '',
      display_name: user.displayName || null,
      photo_url: user.photoURL || null,
      created_at: now,
      updated_at: now,
      last_sign_in: now,
      preferences: {
        ai_platforms: ['chatgpt', 'claude', 'perplexity', 'gemini', 'google_aio'],
        query_categories: [],
        email_notifications: true,
        slack_notifications: false,
        usage_alerts: true,
      },
    };
    await setDoc(userRef, newUserDoc, { merge: true });
  } else {
    // For existing users, only update `updated_at` and `last_sign_in`.
    // We also update fields that might change from the auth provider.
    const updatedUserDoc: Partial<UserDocument> = {
      updated_at: now,
      last_sign_in: now,
      display_name: user.displayName || null,
      photo_url: user.photoURL || null,
    };
    await setDoc(userRef, updatedUserDoc, { merge: true });
  }
};

export function AuthProvider({
  children,
  blockUntilResolved = true,
  fallback = null,
}: {
  children: React.ReactNode;
  blockUntilResolved?: boolean;
  fallback?: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Track previous user state to detect actual sign-ins vs session restoration
  const previousUserRef = useRef<User | null>(null);
  const hasInitializedRef = useRef(false);
  const sessionSyncAbortControllerRef = useRef<AbortController | null>(null);

  const userDocRef = user ? (doc(db, 'users', user.uid) as DocumentReference<UserDocument>) : null;

  const [userDocument] = useDocumentData<UserDocument>(userDocRef);
  const currentUserDocument = userDocument ?? null;

  // Complete signInWithRedirect (Google / OIDC) when the user lands on any page — not only /finish-sign-in.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await authPersistenceReady;
        const result = await getRedirectResult(auth);
        if (result?.user && !cancelled) {
          await syncServerSessionForUser(result.user, { forceRefresh: true });
        }
      } catch (error) {
        console.error('Failed to complete redirect sign-in', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    setAuthSessionDiagnosticsReporter(capture);

    void authPersistenceReady
      .then(persistenceMode => {
        if (cancelled) {
          return;
        }

        if (persistenceMode === 'session' || persistenceMode === 'memory') {
          capture('auth_persistence_degraded', { persistence_mode: persistenceMode });
        }

        unsubscribe = onIdTokenChanged(auth, async nextUser => {
          syncResearchChatRuntimeOwner(nextUser?.uid || null);
          void syncDesktopUpdateToken(nextUser);
          setUser(nextUser);
          setAuthLoading(false);

          if (nextUser) {
            const isInitialAuthCheck = !hasInitializedRef.current;
            const isActualSignIn = hasInitializedRef.current && previousUserRef.current === null;

            sessionSyncAbortControllerRef.current?.abort();
            const syncController = new AbortController();
            sessionSyncAbortControllerRef.current = syncController;

            void ensureServerSessionForUser(nextUser, {
              forceRenew: !isInitialAuthCheck,
              signal: syncController.signal,
              trigger: isInitialAuthCheck ? 'initial' : 'token_change',
            }).catch(handleSessionRecoveryError);

            // This prevents a DB write on every ID token refresh.
            if (isInitialAuthCheck || isActualSignIn) {
              try {
                await createUserDocument(nextUser);
              } catch (error) {
                console.error('Error creating/updating user document:', error);
              }
            }

            const primaryProviderId = nextUser.providerData[0]?.providerId || null;

            identify(nextUser.uid, {
              email: nextUser.email,
              name: nextUser.displayName,
              avatar: nextUser.photoURL,
              provider: primaryProviderId,
            });

            if (isActualSignIn) {
              capture('user_signed_in', {
                method: primaryProviderId || 'unknown',
                is_new_user: nextUser.metadata.creationTime === nextUser.metadata.lastSignInTime,
              });
            }

            setPersonProperties({
              email: nextUser.email,
              name: nextUser.displayName,
              avatar: nextUser.photoURL,
              sign_up_date: nextUser.metadata.creationTime,
              last_sign_in: nextUser.metadata.lastSignInTime,
            });
          } else {
            sessionSyncAbortControllerRef.current?.abort();
            sessionSyncAbortControllerRef.current = null;
            void clearServerSession().catch(error => {
              console.error('Error clearing server auth session:', error);
            });

            if (hasInitializedRef.current && previousUserRef.current !== null) {
              const signOutContext = consumePendingSignOutContext();
              capture(
                'user_signed_out',
                signOutContext || {
                  reason: 'auth_state_cleared',
                  scope: 'browser',
                }
              );
            }
            reset();
          }

          previousUserRef.current = nextUser;
          if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
          }
        });
      })
      .catch(error => {
        console.error('Failed to initialize Firebase auth persistence:', error);
        setAuthLoading(false);
      });

    return () => {
      cancelled = true;
      setAuthSessionDiagnosticsReporter(null);
      sessionSyncAbortControllerRef.current?.abort();
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let hiddenAt: number | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }

      const hiddenDurationMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (hiddenDurationMs < SESSION_VISIBILITY_RECOVERY_THRESHOLD_MS || !auth.currentUser) {
        return;
      }

      void ensureServerSessionForUser(auth.currentUser, {
        trigger: 'visibility',
      }).catch(handleSessionRecoveryError);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({ user, userDocument: currentUserDocument, loading: authLoading }),
    [authLoading, currentUserDocument, user]
  );

  if (authLoading && blockUntilResolved) {
    return fallback ? <>{fallback}</> : <AppLoadingSpinner />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
